import { useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Download, FileDown, FileImage, FileText, Loader2, Printer } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { api, getApiBaseUrl, getErrorMessage } from '@/lib/api'
import { orgTreeApi } from '@/services/org-tree'
import { formatCurrency } from '@/lib/utils'
import type { StoreOrderDetail, StoreOrderPayment } from '@/types'

type CompanyProfile = {
  name: string
  branchName: string
  logoUrl: string
  registrationDetails: string
  email: string
  phone: string
  address: string
  primaryColor: string
}

type ReceiptModel = {
  company: CompanyProfile
  invoiceNumber: string
  receiptNumber: string
  customerName: string
  customerContact: string
  bookingRef: string
  amountText: string
  paymentDate: string
  method: string
  reference: string
  statusLabel: string
  paid: boolean
  particulars: string
  companyContact: string
}

type BookingPaymentReceiptButtonProps = {
  order: StoreOrderDetail
  payment: StoreOrderPayment
  currency?: string | null
  serviceInstanceId?: string | null
  formatPaymentStatus: (status?: string | number | null) => string
  formatPaymentChannelLabel: (value?: string | number | null) => string
  getPaymentDisplayReference: (payment: StoreOrderPayment) => string
  isSuccessfulPaymentStatus: (status?: string | number | null) => boolean
}

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

const compactText = (...parts: Array<string | number | null | undefined>) =>
  parts.map((part) => String(part ?? '').trim()).filter(Boolean).join(', ')

const readString = (source: unknown, keys: string[]) => {
  if (!source || typeof source !== 'object') return ''
  const record = source as Record<string, unknown>
  for (const key of keys) {
    const value = record[key]
    if (typeof value === 'string' && value.trim()) return value.trim()
    if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  }
  return ''
}

const normalizeThemeColor = (value: string) => {
  const trimmed = value.trim()
  if (/^#[0-9a-fA-F]{6}$/.test(trimmed)) return trimmed
  if (/^[0-9a-fA-F]{6}$/.test(trimmed)) return `#${trimmed}`
  return '#0f172a'
}

const parseAmount = (amount?: string | number | null) => {
  if (typeof amount === 'number') {
    return Number.isFinite(amount) ? amount : 0
  }
  if (typeof amount === 'string') {
    const parsed = Number(amount.replace(/[^0-9.-]+/g, ''))
    return Number.isFinite(parsed) ? parsed : 0
  }
  return 0
}

const formatMoney = (amount: number, currency?: string | null) => {
  const marker = String(currency ?? '').trim()
  if (/^[A-Za-z]{3}$/.test(marker)) {
    return formatCurrency(amount, marker.toUpperCase())
  }
  if (marker) {
    const sign = amount < 0 ? '-' : ''
    const formatted = Math.abs(amount).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
    return `${sign}${marker}${formatted}`
  }
  return formatCurrency(amount)
}

const lightenHexColor = (hex: string, amount = 0.82) => {
  const normalized = normalizeThemeColor(hex).replace('#', '')
  const r = Number.parseInt(normalized.slice(0, 2), 16)
  const g = Number.parseInt(normalized.slice(2, 4), 16)
  const b = Number.parseInt(normalized.slice(4, 6), 16)
  const mix = (channel: number) => Math.round(channel + (255 - channel) * amount)
  return `#${[mix(r), mix(g), mix(b)].map((value) => value.toString(16).padStart(2, '0')).join('')}`
}

const hexToRgb = (hex: string): [number, number, number] => {
  const normalized = normalizeThemeColor(hex).replace('#', '')
  return [
    Number.parseInt(normalized.slice(0, 2), 16),
    Number.parseInt(normalized.slice(2, 4), 16),
    Number.parseInt(normalized.slice(4, 6), 16),
  ]
}

const pickContrastText = (hex: string) => {
  const [r, g, b] = hexToRgb(hex)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.62 ? '#0f172a' : '#ffffff'
}

const resolveAbsoluteMediaUrl = (value: string) => {
  const raw = String(value ?? '').trim()
  if (!raw) return ''
  if (/^(https?:|data:|blob:)/i.test(raw)) return raw
  if (raw.startsWith('//')) return `${window.location.protocol}${raw}`
  const apiBase = getApiBaseUrl().replace(/\/+$/, '')
  if (raw.startsWith('/')) return `${apiBase}${raw}`
  return `${apiBase}/${raw}`
}

const renderCompanyLogoHtml = (logoUrl: string, name: string, accent: string) => (
  logoUrl
    ? `<img src="${escapeHtml(resolveAbsoluteMediaUrl(logoUrl))}" alt="${escapeHtml(name)} logo" />`
    : `<div class="logo-fallback" style="color:${escapeHtml(accent)}">${escapeHtml(name.slice(0, 2).toUpperCase())}</div>`
)

const buildCompanyProfile = (
  serviceInstance: unknown,
  fallbackName: string,
  parentServiceInstance?: unknown,
): CompanyProfile => {
  const config = serviceInstance && typeof serviceInstance === 'object'
    ? (serviceInstance as Record<string, unknown>).config
    : null
  const parentConfig = parentServiceInstance && typeof parentServiceInstance === 'object'
    ? (parentServiceInstance as Record<string, unknown>).config
    : null
  const configRecord = config && typeof config === 'object' ? config : null
  const parentConfigRecord = parentConfig && typeof parentConfig === 'object' ? parentConfig : null
  const source = serviceInstance && typeof serviceInstance === 'object' ? serviceInstance : {}
  const parentSource = parentServiceInstance && typeof parentServiceInstance === 'object' ? parentServiceInstance : {}

  const name = readString(configRecord, ['company_name', 'business_name', 'invoice_company_name', 'company_registration_name'])
    || readString(source, ['company_registration_name', 'company_name', 'business_name', 'invoice_company_name', 'name', 'title', 'parent_instance_name'])
    || readString(parentConfigRecord, ['company_name', 'business_name', 'invoice_company_name', 'company_registration_name'])
    || readString(parentSource, ['company_registration_name', 'company_name', 'business_name', 'invoice_company_name', 'name', 'title', 'parent_instance_name'])
    || fallbackName

  const logoUrl = readString(configRecord, ['logo_url', 'company_logo', 'invoice_logo', 'logo'])
    || readString(source, ['logo_url', 'company_logo', 'invoice_logo', 'logo', 'logo_path'])
    || readString(parentConfigRecord, ['logo_url', 'company_logo', 'invoice_logo', 'logo'])
    || readString(parentSource, ['logo_url', 'company_logo', 'invoice_logo', 'logo', 'logo_path'])

  const registrationBody = readString(parentConfigRecord, ['company_registration_body', 'registration_body', 'registered_body', 'tax_registration_body'])
    || readString(parentSource, ['company_registration_body', 'registration_body', 'registered_body', 'tax_registration_body'])
    || readString(configRecord, ['company_registration_body', 'registration_body', 'registered_body', 'tax_registration_body'])
    || readString(source, ['company_registration_body', 'registration_body', 'registered_body', 'tax_registration_body'])

  const registrationName = readString(parentConfigRecord, ['company_registration_name', 'registered_company_name', 'legal_name'])
    || readString(parentSource, ['company_registration_name', 'registered_company_name', 'legal_name'])
    || readString(configRecord, ['company_registration_name', 'registered_company_name', 'legal_name'])
    || readString(source, ['company_registration_name', 'registered_company_name', 'legal_name'])

  const registrationNumber = readString(parentConfigRecord, ['registration_number', 'company_registration_number', 'reg_no', 'tax_registration_number', 'vat_number', 'gst_number'])
    || readString(parentSource, ['registration_number', 'company_registration_number', 'reg_no', 'tax_registration_number', 'vat_number', 'gst_number', 'account_nbr'])
    || readString(configRecord, ['registration_number', 'company_registration_number', 'reg_no', 'tax_registration_number', 'vat_number', 'gst_number'])
    || readString(source, ['registration_number', 'company_registration_number', 'reg_no', 'tax_registration_number', 'vat_number', 'gst_number', 'account_nbr'])

  const registrationDetails = [
    registrationBody,
    registrationName && registrationName !== name ? registrationName : '',
    registrationNumber,
  ].filter(Boolean).join(' · ')

  const email = readString(configRecord, ['invoice_email', 'branch_email', 'contact_email', 'email'])
    || readString(source, ['email', 'branch_email', 'admin_email', 'branch_admin_email', 'primary_role_email', 'owner_email'])
    || readString(parentSource, ['email', 'admin_email', 'owner_email'])

  const phone = readString(configRecord, ['invoice_phone', 'branch_phone', 'contact_phone', 'phone', 'phn_nbr'])
    || readString(source, ['phn_nbr', 'phone', 'branch_phone', 'admin_phone', 'branch_admin_phone', 'primary_role_phone', 'owner_phone'])
    || readString(parentSource, ['phn_nbr', 'phone', 'admin_phone', 'owner_phone'])

  const address = readString(configRecord, ['invoice_address', 'company_address', 'address'])
    || compactText(
      readString(source, ['address_line_1', 'location']),
      readString(source, ['address_line_2']),
      readString(source, ['city']),
      readString(source, ['postcode', 'zipcode']),
      readString(source, ['country_name']),
    )

  const primaryColor = normalizeThemeColor(
    readString(configRecord, ['primary_color', 'brand_color', 'theme_color', 'invoice_primary_color'])
      || readString(source, ['primary_color', 'brand_color', 'theme_color'])
      || '#0f172a',
  )

  const branchName = readString(source, ['name', 'title', 'branch_name', 'service_instance_name'])

  return {
    name,
    branchName: branchName && branchName !== name ? branchName : '',
    logoUrl,
    registrationDetails,
    email,
    phone,
    address,
    primaryColor,
  }
}

const formatReceiptDate = (value?: string | null) => {
  const raw = String(value ?? '').trim()
  if (!raw) return '-'
  const match = raw.match(/^(\d{2})-(\d{2})-(\d{4})(?:\s+(\d{2}):(\d{2}))?/)
  if (match) {
    const [, day, month, year, hour, minute] = match
    return hour != null ? `${day}/${month}/${year} ${hour}:${minute}` : `${day}/${month}/${year}`
  }
  const parsed = new Date(raw.includes(' ') ? raw.replace(' ', 'T') : raw)
  if (Number.isNaN(parsed.getTime())) return raw
  return parsed.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const buildReceiptNumber = (order: StoreOrderDetail, payment: StoreOrderPayment) => {
  const bookingRef = String(order.reference_id || order.id || 'BK').trim()
  const paymentKey = String(payment.payment_reference_id || payment._id || payment.id || 'PMT').trim()
  const shortKey = paymentKey.length > 12 ? paymentKey.slice(-8) : paymentKey
  return `RCP-${bookingRef}-${shortKey}`
}

const buildReceiptModel = (params: {
  order: StoreOrderDetail
  payment: StoreOrderPayment
  company: CompanyProfile
  currency?: string | null
  formatPaymentStatus: (status?: string | number | null) => string
  formatPaymentChannelLabel: (value?: string | number | null) => string
  getPaymentDisplayReference: (payment: StoreOrderPayment) => string
  isSuccessfulPaymentStatus: (status?: string | number | null) => boolean
}): ReceiptModel => {
  const {
    order,
    payment,
    company,
    currency,
    formatPaymentStatus,
    formatPaymentChannelLabel,
    getPaymentDisplayReference,
    isSuccessfulPaymentStatus,
  } = params

  const customerName = order.full_name || order.customer_name || payment.customer_name || 'Customer'
  const customerPhone = order.mobile || order.customer_phone || payment.customer_phone || ''
  const customerEmail = order.email
    || String((order.user_data as { email?: string } | null | undefined)?.email ?? '').trim()
    || order.customer_email
    || payment.customer_email
    || ''
  const method = [
    formatPaymentChannelLabel(payment.payment_service),
    formatPaymentChannelLabel(payment.payment_mode),
    formatPaymentChannelLabel(payment.payment_method),
  ].filter(Boolean).join(' • ') || '-'
  const particulars = (order.order_items ?? [])
    .map((item) => {
      const name = item.product_name || 'Item'
      const qty = Number(item.qty ?? 1) || 1
      return `${name} × ${qty}`
    })
    .join(', ') || 'Booking payment'

  return {
    company,
    invoiceNumber: `BK-${order.reference_id || order.id || 'invoice'}`,
    receiptNumber: buildReceiptNumber(order, payment),
    customerName,
    customerContact: compactText(customerEmail, customerPhone) || '-',
    bookingRef: String(order.reference_id || order.id || '-'),
    amountText: formatMoney(parseAmount(payment.amount), currency ?? payment.currency),
    paymentDate: formatReceiptDate(payment.created_on),
    method,
    reference: getPaymentDisplayReference(payment),
    statusLabel: formatPaymentStatus(payment.payment_status),
    paid: isSuccessfulPaymentStatus(payment.payment_status),
    particulars,
    companyContact: compactText(company.email, company.phone),
  }
}

const loadLogoImage = async (logoUrl: string): Promise<HTMLImageElement | null> => {
  const absolute = resolveAbsoluteMediaUrl(logoUrl)
  if (!absolute) return null

  const blobToImage = (blob: Blob) =>
    new Promise<HTMLImageElement | null>((resolve) => {
      if (!(blob instanceof Blob) || blob.size === 0) {
        resolve(null)
        return
      }
      const objectUrl = URL.createObjectURL(blob)
      const image = new Image()
      image.onload = () => {
        URL.revokeObjectURL(objectUrl)
        resolve(image)
      }
      image.onerror = () => {
        URL.revokeObjectURL(objectUrl)
        resolve(null)
      }
      image.src = objectUrl
    })

  try {
    const apiBase = getApiBaseUrl().replace(/\/+$/, '')
    const requestUrl = absolute.startsWith(apiBase)
      ? absolute.slice(apiBase.length) || '/'
      : absolute
    const response = await api.get(requestUrl, {
      responseType: 'blob',
      ...(absolute.startsWith('http') && !absolute.startsWith(apiBase)
        ? { baseURL: '' }
        : {}),
    })
    const fromApi = await blobToImage(response.data as Blob)
    if (fromApi) return fromApi
  } catch {
    // Continue to image element fallback.
  }

  return await new Promise<HTMLImageElement | null>((resolve) => {
    const image = new Image()
    image.crossOrigin = 'anonymous'
    image.onload = () => resolve(image)
    image.onerror = () => resolve(null)
    image.src = absolute
  })
}

const roundRect = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
) => {
  const r = Math.min(radius, width / 2, height / 2)
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.arcTo(x + width, y, x + width, y + height, r)
  ctx.arcTo(x + width, y + height, x, y + height, r)
  ctx.arcTo(x, y + height, x, y, r)
  ctx.arcTo(x, y, x + width, y, r)
  ctx.closePath()
}

const wrapText = (ctx: CanvasRenderingContext2D, text: string, maxWidth: number) => {
  const words = String(text || '').split(/\s+/).filter(Boolean)
  if (words.length === 0) return ['-']
  const lines: string[] = []
  let current = words[0]
  for (let index = 1; index < words.length; index += 1) {
    const next = `${current} ${words[index]}`
    if (ctx.measureText(next).width <= maxWidth) {
      current = next
    } else {
      lines.push(current)
      current = words[index]
    }
  }
  lines.push(current)
  return lines
}

const renderReceiptCanvas = async (model: ReceiptModel): Promise<HTMLCanvasElement> => {
  const logo = model.company.logoUrl ? await loadLogoImage(model.company.logoUrl) : null
  const accent = model.company.primaryColor
  const accentRgb = hexToRgb(accent)
  const panelBg = lightenHexColor(accent, 0.93)
  const labelColor = lightenHexColor(accent, 0.42)
  const headerText = pickContrastText(accent)

  const width = 1080
  const rows: Array<[string, string]> = [
    ['Received from', model.customerName],
    ['Contact', model.customerContact],
    ['Booking ref', model.bookingRef],
    ['Invoice #', model.invoiceNumber],
    ['Receipt #', model.receiptNumber],
    ['Payment amount', model.amountText],
    ['Payment date', model.paymentDate],
    ['Payment method', model.method],
    ['Reference', model.reference],
    ['Status', model.statusLabel],
    ['Particulars', model.particulars],
  ]

  const measureCanvas = document.createElement('canvas')
  const measureCtx = measureCanvas.getContext('2d')
  if (!measureCtx) {
    throw new Error('Unable to create receipt canvas')
  }
  measureCtx.font = '600 30px Arial'

  let contentHeight = 470
  const companyLines = [
    model.company.branchName,
    model.company.address,
    model.companyContact,
    model.company.registrationDetails,
  ].filter(Boolean)
  contentHeight += companyLines.length * 34
  contentHeight += 150
  for (const [, value] of rows) {
    const lines = wrapText(measureCtx, value, 560)
    contentHeight += Math.max(118, 70 + lines.length * 34)
  }
  contentHeight += 140

  const height = Math.max(1680, contentHeight)
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    throw new Error('Unable to create receipt canvas')
  }

  ctx.fillStyle = '#f8fafc'
  ctx.fillRect(0, 0, width, height)

  roundRect(ctx, 36, 36, width - 72, height - 72, 34)
  ctx.fillStyle = panelBg
  ctx.fill()
  ctx.lineWidth = 4
  ctx.strokeStyle = accent
  ctx.stroke()

  roundRect(ctx, 72, 72, width - 144, 268, 28)
  ctx.fillStyle = accent
  ctx.fill()

  const logoBox = { x: 98, y: 100, size: 160 }
  roundRect(ctx, logoBox.x, logoBox.y, logoBox.size, logoBox.size, 28)
  ctx.fillStyle = '#ffffff'
  ctx.fill()

  if (logo) {
    const pad = 18
    const maxSize = logoBox.size - pad * 2
    const scale = Math.min(maxSize / logo.width, maxSize / logo.height)
    const drawW = logo.width * scale
    const drawH = logo.height * scale
    ctx.drawImage(
      logo,
      logoBox.x + (logoBox.size - drawW) / 2,
      logoBox.y + (logoBox.size - drawH) / 2,
      drawW,
      drawH,
    )
  } else {
    ctx.fillStyle = accent
    ctx.font = '700 42px Arial'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(
      model.company.name.slice(0, 2).toUpperCase(),
      logoBox.x + logoBox.size / 2,
      logoBox.y + logoBox.size / 2,
    )
    ctx.textAlign = 'left'
    ctx.textBaseline = 'alphabetic'
  }

  ctx.fillStyle = headerText
  ctx.font = '700 44px Arial'
  ctx.fillText(model.company.name.slice(0, 42), 290, 150)
  ctx.font = '600 28px Arial'
  ctx.fillText('Payment Receipt', 290, 198)
  ctx.font = '500 22px Arial'
  ctx.fillText(`Invoice ${model.invoiceNumber}`, 290, 240)
  ctx.fillText(`Receipt ${model.receiptNumber}`, 290, 274)

  let y = 380
  ctx.fillStyle = labelColor
  ctx.font = '500 22px Arial'
  for (const line of companyLines) {
    ctx.fillText(String(line).slice(0, 90), 96, y)
    y += 34
  }

  y += 18
  roundRect(ctx, 78, y, width - 156, 126, 22)
  ctx.fillStyle = '#ffffff'
  ctx.fill()
  ctx.fillStyle = labelColor
  ctx.font = '600 20px Arial'
  ctx.fillText('AMOUNT RECEIVED', 104, y + 42)
  ctx.fillStyle = '#0f172a'
  ctx.font = '700 44px Arial'
  ctx.fillText(model.amountText, 104, y + 96)

  roundRect(ctx, width - 280, y + 34, 160, 58, 999)
  ctx.fillStyle = model.paid ? '#ecfdf5' : '#fff7ed'
  ctx.fill()
  ctx.strokeStyle = model.paid ? '#a7f3d0' : '#fed7aa'
  ctx.lineWidth = 2
  ctx.stroke()
  ctx.fillStyle = model.paid ? '#047857' : '#c2410c'
  ctx.font = '700 22px Arial'
  ctx.textAlign = 'center'
  ctx.fillText(model.paid ? 'PAID' : model.statusLabel.slice(0, 12), width - 200, y + 72)
  ctx.textAlign = 'left'

  y += 156

  for (const [label, value] of rows) {
    const valueLines = wrapText(ctx, value, 560)
    const rowHeight = Math.max(118, 70 + valueLines.length * 34)
    roundRect(ctx, 78, y, width - 156, rowHeight, 20)
    ctx.fillStyle = '#ffffff'
    ctx.fill()
    ctx.fillStyle = labelColor
    ctx.font = '600 22px Arial'
    ctx.fillText(`${label}:`, 104, y + 48)
    ctx.fillStyle = '#0f172a'
    ctx.font = '600 30px Arial'
    valueLines.forEach((line, index) => {
      ctx.fillText(line.slice(0, 48), 360, y + 48 + index * 34)
    })
    y += rowHeight + 14
  }

  if (model.paid) {
    ctx.save()
    ctx.translate(width * 0.68, Math.min(y + 40, height - 220))
    ctx.rotate((-18 * Math.PI) / 180)
    ctx.fillStyle = `rgba(${accentRgb[0]}, ${accentRgb[1]}, ${accentRgb[2]}, 0.12)`
    ctx.font = '800 120px Arial'
    ctx.fillText('PAID', 0, 0)
    ctx.restore()
  }

  ctx.fillStyle = labelColor
  ctx.font = '500 20px Arial'
  ctx.textAlign = 'center'
  ctx.fillText(
    `This receipt confirms payment against booking #${model.bookingRef}`,
    width / 2,
    height - 96,
  )
  ctx.textAlign = 'left'

  return canvas
}

const triggerBlobDownload = (blob: Blob, filename: string) => {
  const objectUrl = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = objectUrl
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000)
}

const canvasToBlob = (canvas: HTMLCanvasElement, type: string, quality?: number) =>
  new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Unable to export receipt image'))
        return
      }
      resolve(blob)
    }, type, quality)
  })

export function buildBookingPaymentReceiptHtml(model: ReceiptModel) {
  const accent = model.company.primaryColor
  const panelBg = lightenHexColor(accent, 0.93)
  const labelColor = lightenHexColor(accent, 0.42)
  const headerText = pickContrastText(accent)

  const detailRows: Array<[string, string]> = [
    ['Received from', model.customerName],
    ['Contact', model.customerContact],
    ['Booking ref', model.bookingRef],
    ['Invoice #', model.invoiceNumber],
    ['Receipt #', model.receiptNumber],
    ['Payment amount', model.amountText],
    ['Payment date', model.paymentDate],
    ['Payment method', model.method],
    ['Reference', model.reference],
    ['Status', model.statusLabel],
    ['Particulars', model.particulars],
  ]

  return `
    <!doctype html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>Payment Receipt ${escapeHtml(model.receiptNumber)}</title>
        <style>
          @page { size: A4; margin: 10mm; }
          * { box-sizing: border-box; }
          body { margin: 0; background: linear-gradient(180deg, #eef2ff 0%, #f8fafc 42%, #f8fafc 100%); color: #0f172a; font-family: "Segoe UI", Arial, Helvetica, sans-serif; }
          .page { padding: 24px; }
          .sheet {
            width: min(820px, 100%); margin: 0 auto; background: ${panelBg};
            border: 2px solid ${escapeHtml(accent)}; border-radius: 28px; overflow: hidden;
            box-shadow: 0 24px 60px rgba(15, 23, 42, 0.12);
          }
          .header {
            background: linear-gradient(135deg, ${escapeHtml(accent)} 0%, ${lightenHexColor(accent, 0.18)} 100%);
            color: ${escapeHtml(headerText)}; padding: 28px 30px;
            display: grid; grid-template-columns: 104px 1fr; gap: 20px; align-items: center;
          }
          .brand-mark {
            width: 104px; height: 104px; border-radius: 26px; background: #fff;
            display: flex; align-items: center; justify-content: center; overflow: hidden;
            box-shadow: 0 10px 24px rgba(15, 23, 42, 0.16);
          }
          .brand-mark img { width: 100%; height: 100%; object-fit: contain; padding: 10px; }
          .logo-fallback {
            width: 104px; height: 104px; border-radius: 26px; background: #fff;
            display: flex; align-items: center; justify-content: center; font-size: 32px; font-weight: 800;
          }
          .eyebrow { text-transform: uppercase; letter-spacing: 0.14em; font-size: 11px; opacity: 0.85; margin-bottom: 6px; }
          h1 { margin: 0; font-size: 30px; line-height: 1.15; }
          .subtitle { margin-top: 6px; font-size: 16px; font-weight: 600; }
          .meta { margin-top: 10px; font-size: 13px; opacity: 0.92; }
          .body { padding: 28px 30px 34px; position: relative; }
          .company { color: ${escapeHtml(labelColor)}; font-size: 13px; line-height: 1.55; margin-bottom: 18px; }
          .amount-card {
            background: #fff; border-radius: 20px; padding: 18px 20px; margin-bottom: 16px;
            display: flex; justify-content: space-between; gap: 16px; align-items: center;
            border: 1px solid rgba(15, 23, 42, 0.06); box-shadow: 0 8px 24px rgba(15, 23, 42, 0.04);
          }
          .amount-card .label { font-size: 11px; text-transform: uppercase; letter-spacing: 0.1em; color: ${escapeHtml(labelColor)}; }
          .amount-card .value { margin-top: 4px; font-size: 32px; font-weight: 800; color: #0f172a; }
          .badge {
            display: inline-flex; padding: 8px 14px; border-radius: 999px; font-size: 12px; font-weight: 800;
            background: ${model.paid ? '#ecfdf5' : '#fff7ed'}; color: ${model.paid ? '#047857' : '#c2410c'};
            border: 1px solid ${model.paid ? '#a7f3d0' : '#fed7aa'};
          }
          .details { display: grid; gap: 10px; }
          .detail-row {
            background: #fff; border-radius: 16px; padding: 14px 16px;
            display: grid; grid-template-columns: 150px 1fr; gap: 12px; align-items: start;
            border: 1px solid rgba(15, 23, 42, 0.05);
          }
          .detail-label { font-size: 11px; text-transform: uppercase; letter-spacing: 0.08em; color: ${escapeHtml(labelColor)}; padding-top: 4px; }
          .detail-value { font-size: 15px; font-weight: 600; color: #0f172a; line-height: 1.4; word-break: break-word; }
          .stamp {
            position: absolute; right: 42px; bottom: 110px; transform: rotate(-18deg);
            font-size: 92px; font-weight: 900; color: ${escapeHtml(accent)}; opacity: 0.1; pointer-events: none;
          }
          .footer { margin-top: 26px; text-align: center; color: ${escapeHtml(labelColor)}; font-size: 12px; }
          @media print {
            body { background: #fff; }
            .page { padding: 0; }
            .sheet { box-shadow: none; width: auto; }
          }
        </style>
      </head>
      <body>
        <div class="page">
          <main class="sheet">
            <section class="header">
              <div class="brand-mark">${renderCompanyLogoHtml(model.company.logoUrl, model.company.name, accent)}</div>
              <div>
                <div class="eyebrow">Official receipt</div>
                <h1>${escapeHtml(model.company.name)}</h1>
                <div class="subtitle">Payment Receipt</div>
                <div class="meta">Invoice ${escapeHtml(model.invoiceNumber)} · Receipt ${escapeHtml(model.receiptNumber)}</div>
              </div>
            </section>
            <section class="body">
              <div class="company">
                ${model.company.branchName ? `<div><strong>${escapeHtml(model.company.branchName)}</strong></div>` : ''}
                ${model.company.address ? `<div>${escapeHtml(model.company.address)}</div>` : ''}
                ${model.companyContact ? `<div>${escapeHtml(model.companyContact)}</div>` : ''}
                ${model.company.registrationDetails ? `<div>${escapeHtml(model.company.registrationDetails)}</div>` : ''}
              </div>
              <div class="amount-card">
                <div>
                  <div class="label">Amount received</div>
                  <div class="value">${escapeHtml(model.amountText)}</div>
                </div>
                <span class="badge">${escapeHtml(model.paid ? 'PAID' : model.statusLabel)}</span>
              </div>
              <div class="details">
                ${detailRows.map(([label, value]) => `
                  <div class="detail-row">
                    <div class="detail-label">${escapeHtml(label)}</div>
                    <div class="detail-value">${escapeHtml(value)}</div>
                  </div>
                `).join('')}
              </div>
              ${model.paid ? '<div class="stamp">PAID</div>' : ''}
              <div class="footer">This receipt confirms payment against booking #${escapeHtml(model.bookingRef)}.</div>
            </section>
          </main>
        </div>
      </body>
    </html>
  `
}

export function BookingPaymentReceiptButton({
  order,
  payment,
  currency,
  serviceInstanceId,
  formatPaymentStatus,
  formatPaymentChannelLabel,
  getPaymentDisplayReference,
  isSuccessfulPaymentStatus,
}: BookingPaymentReceiptButtonProps) {
  const previewFrameRef = useRef<HTMLIFrameElement | null>(null)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [exporting, setExporting] = useState<'pdf' | 'png' | null>(null)

  const resolvedServiceInstanceId = String(
    serviceInstanceId
    || order.service_instance_id
    || order.service_instance
    || '',
  ).trim()

  const serviceInstanceQuery = useQuery({
    queryKey: ['booking-invoice-service-instance', resolvedServiceInstanceId],
    queryFn: () => orgTreeApi.getOptionalServiceInstance(resolvedServiceInstanceId),
    enabled: Boolean(resolvedServiceInstanceId),
    staleTime: 120_000,
  })
  const parentServiceInstanceId = serviceInstanceQuery.data?.parent_instance_id
  const parentServiceInstanceQuery = useQuery({
    queryKey: ['booking-invoice-parent-service-instance', parentServiceInstanceId, resolvedServiceInstanceId],
    queryFn: () => orgTreeApi.getOptionalServiceInstance(parentServiceInstanceId!),
    enabled: Boolean(parentServiceInstanceId && parentServiceInstanceId !== resolvedServiceInstanceId),
    staleTime: 120_000,
  })

  const companyProfile = useMemo(() => {
    const fallback = String(order.service_instance_name || 'Ellan One').trim() || 'Ellan One'
    return buildCompanyProfile(serviceInstanceQuery.data, fallback, parentServiceInstanceQuery.data)
  }, [order.service_instance_name, parentServiceInstanceQuery.data, serviceInstanceQuery.data])

  const receiptModel = useMemo(() => buildReceiptModel({
    order,
    payment,
    company: companyProfile,
    currency,
    formatPaymentStatus,
    formatPaymentChannelLabel,
    getPaymentDisplayReference,
    isSuccessfulPaymentStatus,
  }), [
    companyProfile,
    currency,
    formatPaymentChannelLabel,
    formatPaymentStatus,
    getPaymentDisplayReference,
    isSuccessfulPaymentStatus,
    order,
    payment,
  ])

  const receiptHtml = useMemo(() => buildBookingPaymentReceiptHtml(receiptModel), [receiptModel])
  const baseFileName = `booking-payment-receipt-${receiptModel.receiptNumber}`.replace(/[^a-zA-Z0-9-_]+/g, '-')

  const downloadPng = async () => {
    setExporting('png')
    try {
      const canvas = await renderReceiptCanvas(receiptModel)
      const blob = await canvasToBlob(canvas, 'image/png')
      triggerBlobDownload(blob, `${baseFileName}.png`)
      toast.success('Payment receipt image downloaded')
    } catch (error) {
      toast.error(getErrorMessage(error) || 'Unable to download receipt image')
    } finally {
      setExporting(null)
    }
  }

  const downloadPdf = async () => {
    setExporting('pdf')
    try {
      const [{ jsPDF }] = await Promise.all([import('jspdf')])
      const canvas = await renderReceiptCanvas(receiptModel)
      const imageData = canvas.toDataURL('image/png')
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'pt',
        format: 'a4',
      })
      const pageWidth = pdf.internal.pageSize.getWidth()
      const pageHeight = pdf.internal.pageSize.getHeight()
      const margin = 24
      const usableWidth = pageWidth - margin * 2
      const usableHeight = pageHeight - margin * 2
      const scale = Math.min(usableWidth / canvas.width, usableHeight / canvas.height)
      const drawWidth = canvas.width * scale
      const drawHeight = canvas.height * scale
      const offsetX = (pageWidth - drawWidth) / 2
      const offsetY = margin
      pdf.addImage(imageData, 'PNG', offsetX, offsetY, drawWidth, drawHeight, undefined, 'FAST')
      pdf.save(`${baseFileName}.pdf`)
      toast.success('Payment receipt PDF downloaded')
    } catch (error) {
      toast.error(getErrorMessage(error) || 'Unable to download receipt PDF')
    } finally {
      setExporting(null)
    }
  }

  const printFromPreview = () => {
    const frameWindow = previewFrameRef.current?.contentWindow
    if (!frameWindow) {
      toast.error('Preview is still loading. Please try again.')
      return
    }
    frameWindow.focus()
    frameWindow.print()
  }

  return (
    <>
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="h-8 gap-1.5 px-2 text-xs"
        onClick={() => setPreviewOpen(true)}
        aria-label="Payment receipt"
        title="Payment receipt"
      >
        <FileDown className="h-3.5 w-3.5" />
        Receipt
      </Button>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-h-[92vh] max-w-3xl overflow-hidden p-0">
          <DialogHeader className="border-b px-6 py-4">
            <DialogTitle>Payment receipt</DialogTitle>
          </DialogHeader>
          <iframe
            ref={previewFrameRef}
            title="Payment receipt preview"
            srcDoc={receiptHtml}
            className="h-[70vh] w-full border-0 bg-muted"
          />
          <DialogFooter className="flex-col gap-2 border-t px-6 py-4 sm:flex-row sm:justify-between">
            <Button type="button" variant="outline" onClick={() => setPreviewOpen(false)}>Close</Button>
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" onClick={printFromPreview}>
                <Printer className="mr-2 h-4 w-4" />
                Print
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button type="button" disabled={exporting != null}>
                    {exporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
                    Download
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    disabled={exporting != null}
                    onSelect={(event) => {
                      event.preventDefault()
                      void downloadPdf()
                    }}
                  >
                    <FileText className="mr-2 h-4 w-4" />
                    Download PDF
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    disabled={exporting != null}
                    onSelect={(event) => {
                      event.preventDefault()
                      void downloadPng()
                    }}
                  >
                    <FileImage className="mr-2 h-4 w-4" />
                    Download image (PNG)
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
